<template>
  <PageContainer :title="$t('zui-jin-zuo-pin')">
    <ListCard
      ref="cardListRef"
      :list="recentList"
      :editable-config="editableConfig"
      :max="MAX_NUM"
      :total="total"
      :loading="loading.normal"
      class="content"
      :noMore="noMore"
      @load-more="loadMore"
      @card-click="onCoverClick"
    >
      <template #menu="{ dataSource }">
        <!-- <Eye :data="dataSource" class="clear-collect-position" /> -->
        <a-menu mode="vertical" @click="(e) => onMenuClick(e, dataSource)">
          <a-menu-item
            v-if="
              dataSource?.lastGenerate?.video_url &&
              checkPermission(ActionListKey.RECENTLY_PLAY)
            "
            key="play"
          >
            {{ $t('bo-fang-shi-pin') }}
          </a-menu-item>

          <a-menu-item
            v-if="checkPermission(ActionListKey.RECENTLY_COPY)"
            key="copy"
          >
            {{ $t('fu-zhi-dao-tuan-dui-kong-jian') }}
          </a-menu-item>
          <a-menu-item key="rename">{{ $t('zhong-ming-ming') }}</a-menu-item>
          <a-menu-item key="delete">
            {{ $t('cong-zui-jin-zuo-pin-yi-chu') }}
          </a-menu-item>
        </a-menu>
      </template>
      <template #batch="{ checkable, checkedList }">
        <div v-if="checkable">
          <a-button
            v-if="checkPermission(ActionListKey.RECENTLY_COPY)"
            class="mr-[10px]"
            :disabled="checkedList.length === 0"
            ghost
            type="primary"
            @click="handleCopyConfirm(checkedList)"
          >
            {{ $t('fu-zhi-dao-tuan-dui-kong-jian') }}
          </a-button>
          <a-button
            ghost
            type="primary"
            :disabled="checkedList.length === 0"
            @click="onDelete(checkedList)"
          >
            {{ $t('cong-zui-jin-zuo-pin-zhong-yi-chu') }}
          </a-button>
        </div>
      </template>
      <template #cover="{ dataSource }">
        <div class="tool">
          <div
            class="icon-item collection"
            :class="{ favorite: dataSource.like }"
          >
            <XCollect
              :model-value="dataSource.like"
              class="collect"
              :params="{
                model_id: dataSource.id,
                model_type: COLLECTION_MAP.OPUS,
                source: COLLECTION_SOURCE.PERSON,
              }"
              :data="dataSource"
              :class="{ active: true }"
              @reload="changeCollection"
            />
          </div>
          <div
            v-if="checkPermission(ActionListKey.RECENTLY_SHARE)"
            class="share"
          >
            <TemplateShare
              :id="dataSource?.id"
              :isCard="true"
              :title="$t('fen-xiang-kong-ge') + $t('zuo-pin')"
              :showExtra="false"
              :params="{
                share_ids: [dataSource.id],
                model_type: COLLECTION_MAP.OPUS,
              }"
              :trackParams="{
                target: TARGET_FROM.Project,
                dataId: dataSource.id,
                dataTitle: dataSource.title,
              }"
              @click.stop
            />
          </div>
        </div>
        <div class="opus">
          <ProjectEye :data="dataSource" />
        </div>
      </template>
      <template #empty>
        <div class="text-12 leading-[19px] text-font-60 mt-[8px] font-medium">
          {{ $t('ni-zui-jin-bian-ji-guo-de-zuo-pin-hui-xian-shi-zai-ci-chu') }}
        </div>
      </template>
    </ListCard>
  </PageContainer>
</template>

<script lang="ts" setup>
import { debounce, throttle } from 'lodash-es'
import { getRecentList, deleteRecent } from '~/api/module/recent'
import { editOpus } from '~/api/module/opus'
import type { IMediaCard } from '~/components/media'
import { useCopyToTeam } from '~/hooks/useModal/modals/copyToTeam'
import { useRequestAuth } from '~/hooks/useRequestAuth'
import { useActionIntercept } from '~/hooks/useActionIntercept'
import { useVideoView } from '~/hooks/useModal/modals/view'
import XCollect from '@/features/collect/index.vue'
import ProjectEye from '@/features/pageList/components/preview/index.vue'
import { PAGE_SIZE } from '~/constants'
import { TemplateShare } from '@/features/template/components/templateShare'

const loading = ref({
  normal: false,
})
const MAX_NUM = 50
const recentList = ref<any[]>([])
const total = ref(0)
const cardListRef = ref()
const queryParams = ref({
  page: 1,
  page_size: PAGE_SIZE,
})
const pollTaskRef = ref()
const isMoreMenu = ref(false)
const noMore = ref(false)
const { $t } = useNuxtApp()

// 获取最近作品
const getOpusList = async (skip = false) => {
  loading.value.normal = true
  const res: any = await useRequestAuth(() => getRecentList(queryParams.value))

  if (res.data) {
    const arrData = transformList(res.data.results || []).map((item: any) => ({
      ...item,
      type: CARD_TYPE.MAIN,
      name: '',
      time: $t('bian-ji-yu-timeformattextnew-datedataupdatetimegettime', [
        item.time,
      ]),
      page: Number(queryParams.value.page),
    }))
    loading.value.normal = false
    total.value = res.data.count

    if (res.data.next && Number(queryParams.value.page) <= 2) {
      const temp: any = getUrlParams(res.data.next)
      queryParams.value = temp
    }
    if (skip) {
      recentList.value = arrData
    } else {
      for (const item of arrData) {
        const index = recentList.value.findIndex(
          (record: any) => record.id === item.id
        )
        if (index === -1) {
          recentList.value.push(item)
        } else {
          recentList.value[index] = item
        }
      }
    }

    if (recentList.value.length >= total.value) {
      noMore.value = true
    }
  } else {
    loading.value.normal = false
  }

  loading.value.normal = false
}

// reload
const handleReload = debounce((flag = false) => {
  queryParams.value.page = 1
  getOpusList(true)
  if (flag) {
    pollTaskRef.value?.restart?.()
  }
}, 1000)

// 封面点击
const onCoverClick = (record: IMediaCard) => {
  trackEvent(TRACK_EVENT.OPEN_OPUS, {
    target: TARGET_FROM.Project,
    source: SOURCE.RECENTLY,
    entrance: OPEN_ENTRANCE.CARD,
    permission: record.limited ? 'read' : 'edit',
  })
  window.location.href = `/editor/${record.id}/${record.secret_id}`
}

const onMenuClick = (keys: { keyPath: string[] }, record: IMediaCard) => {
  const [key, key1] = keys.keyPath
  switch (key) {
    case 'open-and-locate':
      // 打开并定位
      window.location.href = `/editor/${record.id}/${record.secret_id}?default=${key1}`
      break
    case 'copy':
      if (!useActionIntercept()) {
        useCopyToTeam({
          ids: [Number(record.id)],
          model_type: COLLECTION_MAP.OPUS,
        })
      }
      break
    case 'play':
      playVideo(record.lastGenerate?.video_url, OPEN_ENTRANCE.MORE)
      break
    case 'share':
      useShareModal($t('fen-xiang-zuo-pin'), {
        share_ids: [record.id],
        model_type: COLLECTION_MAP.OPUS,
        trackParams: {
          target: TARGET_FROM.Project,
          source: SOURCE.RECENTLY,
        },
      })
      break
    case 'rename':
      isMoreMenu.value = true

      record.edit = true
      break
    case 'delete':
      onDelete([Number(record.id)])
      break
    default:
      break
  }
}

// 播放视频
const playVideo = (url: string, click: any) => {
  trackEvent(TRACK_EVENT.CLICK_PLAY_VIDEO, {
    click,
    target: TARGET_FROM.Project,
    source: SOURCE.RECENTLY,
  })
  useVideoView(url)
}

// 加载更多
const loadMore = throttle(() => {
  console.log(queryParams.value.page)
  if (Number(queryParams.value.page) > 2) {
    noMore.value = true
    return
  }

  if (Number(queryParams.value.page) <= 2) {
    getOpusList()
  }
}, 2000)

// 复制到团队空间
const handleCopyConfirm = (ids: number[]) => {
  if (useActionIntercept()) {
    return
  }

  useCopyToTeam(
    {
      ids,
      model_type: COLLECTION_MAP.OPUS,
    },
    {
      onOk: () => {
        cardListRef.value?.reset()
      },
    }
  )
}

const editableConfig: any = {
  onSave: async (value: string, record: IMediaCard) => {
    trackEvent(TRACK_EVENT.RENAME_TARGET, {
      target: TARGET_FROM.Project,
      source: SOURCE.RECENTLY,
      click: isMoreMenu.value ? OPEN_ENTRANCE.MORE : OPEN_ENTRANCE.CARD,
    })
    isMoreMenu.value = false

    try {
      const res = await editOpus({
        name: value,
        id: Number(record.id),
      })

      if (!res.code) {
        message.success($t('bao-cun-cheng-gong'))
      }

      record.title = value
    } catch (error) {
      console.error(error)

      // record.title = record.title
    }
  },
}

// 收藏回调
const changeCollection = (id: number) => {
  recentList.value = recentList.value.map((item) => {
    return {
      ...item,
      like: id === item.id ? !item.like : item.like,
    }
  })
}

// 批量删除
const onDelete = async (ids: number[]) => {
  const res = await deleteRecent(ids)

  trackEvent(TRACK_EVENT.REMOVE_OPUS_FROM_RECENTLY, {
    Batch: ids?.length > 1 ? BATCH.ALL : BATCH.ONE,
    ids: ids.join(','),
  })
  if (!res.code) {
    const indexKey = recentList.value
      .filter((item) => ids.includes(item.id))
      .map((o) => o.page)
    recentList.value = recentList.value.filter((item) => !ids.includes(item.id))
    message.success($t('yi-chu-cheng-gong-0'))
    if (!indexKey.includes(1)) {
      getOpusList()
    } else {
      cardListRef.value?.reset()
      handleReload()
    }
  }
}

provideResourceLocation({
  source: SOURCE.RECENTLY,
})

onMounted(() => {
  getOpusList()
})
</script>

<style lang="scss" scoped>
.content {
  @apply h-full;
  :deep(.list-card) {
    overflow-y: auto;
    padding-inline-end: 10px;
    padding-block-end: 50px;
    // height: 100%;
  }

  .icon-item {
    @apply flex justify-center items-center w-[28px] h-[28px] rounded-[50%] text-center cursor-pointer text-primary;
    opacity: 0.8;
    color: var(--text-primary-reverse);
  }
  .tool {
    @apply absolute flex gap-x-[10px] top-[10px] left-[10px];
    .collection,
    .share,
    .eye {
      opacity: 0;
    }

    .favorite {
      opacity: 1;
    }
  }

  .opus {
    @apply absolute top-[10px] right-[44px] opacity-0;
  }

  :deep(.base-card) {
    &:hover {
      .collection,
      .eye,
      .opus,
      .share,
      .play-button {
        opacity: 1;
      }
    }
  }

  // .list-card {
  //   height: calc(100vh - 300px);
  // }

  .list-container {
    :deep(.ant-checkbox-group) {
      height: calc(100% - 52px);
      overflow-y: auto;
    }
    :deep(.list-card) {
      // height: calc(100vh - 280px);
      overflow-y: auto;
      padding-inline-end: 10px;
      padding-block-end: 65px;
    }
  }
}
</style>
